import React, { useEffect, useState } from 'react';
import { X, Package, Layers, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../contexts/I18nProvider';
import { itemLocationBalanceService } from '../../../services/itemLocationBalanceService';
import { ItemLocationBalanceDto } from '../../../types/itemLocationBalance';
import { BinLocationGroup, BinDescriptionResolvers } from './types';

/**
 * DetailPanel — persistent detaljpanel for valgt lokasjon (RackGridV2)
 *
 * Erstatter hover-tooltip som primærkanal for detaljer: klikk på en celle åpner
 * panelet, som viser alle attributter med lesbare beskrivelser, skuffliste for
 * 4D-celler, og faktisk innhold (ItemBins) hentet ved åpning.
 */

interface DetailPanelProps {
    group: BinLocationGroup;
    onClose: () => void;
    describe: BinDescriptionResolvers;
    /** Valgfrie handlingsknapper (f.eks. kryssnavigasjon) — rendres øverst i panelet */
    actions?: React.ReactNode;
}

export const DetailPanel: React.FC<DetailPanelProps> = ({ group, onClose, describe, actions }) => {
    const { t } = useTranslation();
    const [contents, setContents] = useState<ItemLocationBalanceDto[] | null>(null);
    const [isLoadingContents, setIsLoadingContents] = useState(false);
    const [contentsError, setContentsError] = useState<string | null>(null);
    // For 4D-celler: hvilken skuff som vises i attributt-/innholdsseksjonen
    const [activeBinCode, setActiveBinCode] = useState(group.primaryLocation.binCode);

    useEffect(() => {
        setActiveBinCode(group.primaryLocation.binCode);
    }, [group]);

    const location = group.locations.find(l => l.binCode === activeBinCode) ?? group.primaryLocation;

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsLoadingContents(true);
            setContentsError(null);
            setContents(null);
            try {
                const rows = await itemLocationBalanceService.getItemBinsByBinCode(location.binCode);
                if (!cancelled) setContents(rows.filter(r => (r.quantity ?? 0) !== 0));
            } catch (err) {
                if (!cancelled) setContentsError(err instanceof Error ? err.message : 'Kunne ikke hente innhold');
            } finally {
                if (!cancelled) setIsLoadingContents(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [location.binCode]);

    const yesNo = (value?: boolean): string =>
        value
            ? t('manager.modules.rackBuilder.common.yes', {}, 'Ja')
            : t('manager.modules.rackBuilder.common.no', {}, 'Nei');

    const Row: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
        if (value === undefined || value === null || value === '') return null;
        return (
            <div className="flex justify-between gap-2 text-xs py-0.5">
                <span className="text-gray-500 flex-shrink-0">{label}</span>
                <span className="text-gray-800 text-right break-words min-w-0">{value}</span>
            </div>
        );
    };

    return (
        <div className="w-72 flex-shrink-0 bg-white rounded-lg border border-gray-300 flex flex-col min-h-0 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <div className="min-w-0">
                    <div className="font-mono font-bold text-sm text-gray-800 truncate">{location.binCode}</div>
                    {location.description1 && (
                        <div className="text-xs text-gray-600 truncate">{location.description1}</div>
                    )}
                </div>
                <button
                    onClick={onClose}
                    className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-200 rounded flex-shrink-0"
                    title={t('common.buttons.close', {}, 'Lukk')}
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {actions && (
                    <div className="flex flex-wrap gap-1.5">
                        {actions}
                    </div>
                )}

                {/* 4D: skuffvelger */}
                {group.hasMultipleDrawers && (
                    <div>
                        <div className="flex items-center space-x-1 text-xs font-semibold text-purple-700 mb-1">
                            <Layers className="w-3.5 h-3.5" />
                            <span>{t('manager.rackGrid.detail.drawers', { '0': group.drawerCount }, '{0} skuffer')}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {group.locations.map(loc => (
                                <button
                                    key={loc.binCode}
                                    onClick={() => setActiveBinCode(loc.binCode)}
                                    className={`px-2 py-0.5 text-[10px] font-mono rounded border ${
                                        loc.binCode === activeBinCode
                                            ? 'bg-purple-100 border-purple-400 text-purple-800'
                                            : 'bg-white border-gray-300 text-gray-700 hover:border-purple-300'
                                    }`}
                                >
                                    {loc.drawer ?? loc.binCode}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Attributter med lesbare beskrivelser */}
                <div>
                    <div className="text-xs font-semibold text-gray-700 uppercase mb-1">
                        {t('manager.rackGrid.detail.attributes', {}, 'Egenskaper')}
                    </div>
                    <Row label={t('manager.modules.rackBuilder.fields.binSize', {}, 'Bin Storrelse')} value={describe.binSize(location.binSizeNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.binType', {}, 'Bin Type')} value={describe.binType(location.binTypeNo)} />
                    <Row label={t('warehouse.general.zone', {}, 'Sone')} value={describe.zone(location.stcZoneID)} />
                    <Row label={t('manager.modules.rackBuilder.fields.binGroup', {}, 'Bin Gruppe')} value={describe.binGrp(location.binGrpNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.binCategory', {}, 'Lokasjonskategori')} value={describe.binCat(location.stcBinCatNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.pickPriority', {}, 'Plukk Prioritet')} value={describe.priority(location.pickPriorityNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.putAwayPriority', {}, 'Put-away Prioritet')} value={describe.priority(location.putAwayPriorityNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.resource', {}, 'Ressurs')} value={describe.resource(location.resourceNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.pickable', {}, 'Plukkbar')} value={yesNo(location.pickableYesNo)} />
                    <Row label={t('manager.modules.rackBuilder.fields.reservation', {}, 'Reservasjon')} value={yesNo(location.reservationAllowed)} />
                    <Row label={t('manager.modules.rackBuilder.fields.autoReplenishment', {}, 'Auto-pafylling')} value={yesNo(location.autoReplenishment)} />
                    <Row label={t('manager.modules.rackBuilder.fields.sharedBin', {}, 'Delt lokasjon')} value={yesNo(location.sharedBinYesNo)} />
                    {location.itemNo && (
                        <Row
                            label={t('manager.rackGrid.detail.dedicatedTo', {}, 'Dedikert til')}
                            value={<span className="text-green-700 font-semibold">{location.itemNo}</span>}
                        />
                    )}
                </div>

                {/* Innhold (ItemBins) */}
                <div>
                    <div className="flex items-center space-x-1 text-xs font-semibold text-gray-700 uppercase mb-1">
                        <Package className="w-3.5 h-3.5" />
                        <span>{t('manager.rackGrid.detail.contents', {}, 'Innhold')}</span>
                    </div>
                    {isLoadingContents ? (
                        <div className="flex items-center text-xs text-gray-500 py-1">
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                            {t('common.status.loading', {}, 'Laster...')}
                        </div>
                    ) : contentsError ? (
                        <div className="text-xs text-red-700">{contentsError}</div>
                    ) : !contents || contents.length === 0 ? (
                        <div className="text-xs text-gray-500 italic">
                            {t('manager.rackGrid.detail.emptyBin', {}, 'Tom lokasjon — ingen saldo')}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {contents.map(row => (
                                <div key={row.id} className="border border-gray-200 rounded p-1.5">
                                    <div className="flex justify-between gap-2 text-xs">
                                        <span className="font-mono font-semibold text-gray-800 truncate">{row.itemNo}</span>
                                        <span className="text-gray-800 whitespace-nowrap font-semibold">{row.quantity}</span>
                                    </div>
                                    {row.itemDescription && (
                                        <div className="text-[10px] text-gray-600 truncate">{row.itemDescription}</div>
                                    )}
                                    <div className="flex justify-between text-[10px] text-gray-500">
                                        {row.batchNo && <span>{t('manager.rackGrid.detail.batch', { '0': row.batchNo }, 'Batch: {0}')}</span>}
                                        {(row.quantityReserved ?? 0) > 0 && (
                                            <span>{t('manager.rackGrid.detail.reserved', { '0': row.quantityReserved }, 'Reservert: {0}')}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
